import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    TextField,
    Autocomplete,
    Card,
    CardContent,
    IconButton,
    Divider,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import TextFieldWithMath from './TextFieldWithMath';
import QuestionRender from '../../components/QuestionRender';
import { CoordinateGridAdmin, CoordinateGrid, defaultCoordinateConfig, formatElementSummary, AnswerLogicSummary } from '../../components/coordinateGrid';
import { TableAdmin, TableAnswerLogicSummary, defaultTableConfig, extractAnswerFromConfig } from '../../components/tableAnswer';
import { NumberLineAdmin, NumberLine, defaultNumberLineConfig, formatElementSummary as formatNumberLineElementSummary } from '../../components/numberLine';
import { MappingAdmin, MappingDiagram, defaultMappingConfig, parseAnswer as parseMappingAnswer } from '../../components/mappingAnswer';
import { GRADING_TYPES, GRADING_TYPE_LABELS, ANSWER_TOOLS } from '../../utils/hwConstants';
import { PolynomialGraphAdmin, defaultPolynomialGraphConfig, parseFactoredPolynomial, generateGraphVariations } from '../../components/polynomialGraph';
import { RationalGraphAdmin, defaultRationalGraphConfig, parseFactoredRational, generateRationalGraphVariations } from '../../components/rationalGraph';
import { normalizeStep1Types } from '../../components/rationalGraph/rationalGraphUtils';
import { TrigGraphAdmin, defaultTrigGraphConfig, parseTrigEquation, generateTrigGraphVariations } from '../../components/trigGraph';
import { MultilineAdmin, MultilineReadOnly, defaultMultilineConfig } from '../../components/multiline';
import {
    defaultMathliveConfig,
    MATHLIVE_ANSWER_TYPES,
    parseMathliveAnswer,
    stringifyMathliveAnswer,
    wrapLatexDelimiters,
    MathAnswerSpecEditor,
} from '../../components/mathlive';
import 'mathlive';

const ExtractedQuestionsReview = ({ questions: initialQuestions, textbooks, extractionJobId, onSave, onCancel, onError, onSaveDraft, onPageClick }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [questions, setQuestions] = useState(initialQuestions);
    const [saving, setSaving] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [resolvingIndex, setResolvingIndex] = useState(null);
    // Global setting to render math answers as LaTeX - default true if any question uses mathlive
    const [renderAsLatex, setRenderAsLatex] = useState(() =>
        initialQuestions.some(q => q.answer_tool === 'mathlive')
    );

    const questionTypes = [
        { label: 'Multiple Choice', value: 'multiple_choice' },
        { label: 'Short Answer', value: 'short_answer' },
        { label: 'Long Answer', value: 'long_answer' },
        { label: 'File Upload', value: 'file_upload' },
    ];

    const answerToolsWithNone = [{ label: 'None', value: null }, ...ANSWER_TOOLS];

    const updateQuestion = (index, field, value) => {
        // Use functional update to ensure we're working with the latest state
        // This prevents race conditions when multiple updates happen in quick succession
        setQuestions(prevQuestions => {
            const updatedQuestions = [...prevQuestions];
            updatedQuestions[index] = {
                ...updatedQuestions[index],
                [field]: value,
            };
            return updatedQuestions;
        });
    };

    // Helper to check if config is a valid table config
    const isValidTableConfig = (config) => {
        return config &&
               Array.isArray(config.columns) &&
               config.columns.length > 0 &&
               Array.isArray(config.rows);
    };

    // Helper function to handle answer_tool changes with proper initialization
    const handleAnswerToolChange = (index, newTool) => {
        const updatedQuestions = [...questions];
        const currentConfig = updatedQuestions[index].answer_config;

        updatedQuestions[index] = {
            ...updatedQuestions[index],
            answer_tool: newTool,
        };

        // Initialize config and answer for table tool if needed
        // Use validation helper to check for valid table structure
        if (newTool === 'table' && !isValidTableConfig(currentConfig)) {
            const freshTableConfig = JSON.parse(JSON.stringify(defaultTableConfig));
            updatedQuestions[index].answer_config = freshTableConfig;
            updatedQuestions[index].answer = [JSON.stringify(extractAnswerFromConfig(freshTableConfig))];
        }

        // Initialize config and answer for coordinate tool if needed
        // Check if config exists and has coordinate structure (grid object)
        if (newTool === 'coordinate' && (!currentConfig || !currentConfig.grid)) {
            const freshCoordinateConfig = JSON.parse(JSON.stringify(defaultCoordinateConfig));
            updatedQuestions[index].answer_config = freshCoordinateConfig;
            updatedQuestions[index].answer = [JSON.stringify({ elements: [] })];
        }

        // Initialize config and answer for numberline tool if needed
        // Check if config exists and has numberline structure (min/max properties)
        if (newTool === 'numberline' && (!currentConfig || currentConfig.min === undefined)) {
            const freshNumberLineConfig = JSON.parse(JSON.stringify(defaultNumberLineConfig));
            updatedQuestions[index].answer_config = freshNumberLineConfig;
            updatedQuestions[index].answer = [JSON.stringify({ elements: [] })];
        }

        // Initialize config and answer for mapping tool if needed
        // Check if config exists and has mapping structure (leftItems/rightItems arrays)
        if (newTool === 'mapping' && (!currentConfig || !currentConfig.leftItems)) {
            const freshMappingConfig = JSON.parse(JSON.stringify(defaultMappingConfig));
            updatedQuestions[index].answer_config = freshMappingConfig;
            updatedQuestions[index].answer = [''];
        }

        // Initialize config and answer for polynomialGraph tool if needed
        // Check if config exists and has polynomialGraph structure (type property)
        if (newTool === 'polynomialGraph' && (!currentConfig || currentConfig.type !== 'polynomialGraphSelection')) {
            const freshPolynomialGraphConfig = JSON.parse(JSON.stringify(defaultPolynomialGraphConfig));
            updatedQuestions[index].answer_config = freshPolynomialGraphConfig;
            updatedQuestions[index].answer = [JSON.stringify({ correctGraphIndex: 0 })];
        }

        // Initialize config and answer for rationalGraph tool if needed
        if (newTool === 'rationalGraph' && (!currentConfig || currentConfig.type !== 'rationalGraphSelection')) {
            const freshRationalGraphConfig = JSON.parse(JSON.stringify(defaultRationalGraphConfig));
            updatedQuestions[index].answer_config = freshRationalGraphConfig;
            updatedQuestions[index].answer = [JSON.stringify({ correctGraphIndex: 0 })];
        }

        // Initialize config and answer for trigGraph tool if needed
        if (newTool === 'trigGraph' && (!currentConfig || currentConfig.type !== 'trigGraphSelection')) {
            const freshTrigGraphConfig = JSON.parse(JSON.stringify(defaultTrigGraphConfig));
            updatedQuestions[index].answer_config = freshTrigGraphConfig;
            updatedQuestions[index].answer = [JSON.stringify({ correctGraphIndex: 0 })];
        }

        // Initialize config and answer for multiline tool if needed
        const isMultilineConfig = currentConfig
            && typeof currentConfig.inputType === 'string'
            && typeof currentConfig.gradingMode === 'string';
        if (newTool === 'multiline' && !isMultilineConfig) {
            const freshConfig = JSON.parse(JSON.stringify(defaultMultilineConfig));
            updatedQuestions[index].answer_config = freshConfig;
            updatedQuestions[index].answer = [JSON.stringify({ rows: [] })];
        }

        const isMathliveConfig = currentConfig
            && (typeof currentConfig.allowTextInput === 'boolean' || currentConfig.spec);
        if (newTool === 'mathlive' && !isMathliveConfig) {
            updatedQuestions[index].answer_config = { ...defaultMathliveConfig };
        }

        // Clear config when switching to a non-specialized tool
        if (!['table', 'coordinate', 'numberline', 'mapping', 'polynomialGraph', 'rationalGraph', 'trigGraph', 'multiline', 'mathlive'].includes(newTool)) {
            updatedQuestions[index].answer_config = null;
        }

        setQuestions(updatedQuestions);
    };

    const updateOption = (questionIndex, optionIndex, value) => {
        const updatedQuestions = [...questions];
        const options = [...(updatedQuestions[questionIndex].options || [])];
        options[optionIndex] = value;
        updatedQuestions[questionIndex].options = options;
        setQuestions(updatedQuestions);
    };

    const addOption = (questionIndex) => {
        const updatedQuestions = [...questions];
        const options = updatedQuestions[questionIndex].options || [];
        updatedQuestions[questionIndex].options = [...options, ''];
        setQuestions(updatedQuestions);
    };

    const removeOption = (questionIndex, optionIndex) => {
        const updatedQuestions = [...questions];
        const options = [...updatedQuestions[questionIndex].options];
        options.splice(optionIndex, 1);
        updatedQuestions[questionIndex].options = options;
        setQuestions(updatedQuestions);
    };

    const updateAnswer = (questionIndex, answerIndex, value) => {
        const updatedQuestions = [...questions];
        const answers = [...(updatedQuestions[questionIndex].answer || [])];
        const oldValue = answers[answerIndex] || '';
        answers[answerIndex] = value;
        updatedQuestions[questionIndex].answer = answers;
        setQuestions(updatedQuestions);

        // If user removed LaTeX delimiters, uncheck renderAsLatex
        const hadDelimiters = /\\\(|\\\)|\$/.test(oldValue);
        const hasDelimiters = /\\\(|\\\)|\$/.test(value);
        if (hadDelimiters && !hasDelimiters) {
            setRenderAsLatex(false);
        }
    };

    const addAnswer = (questionIndex) => {
        const updatedQuestions = [...questions];
        const answers = updatedQuestions[questionIndex].answer || [];
        updatedQuestions[questionIndex].answer = [...answers, ''];
        setQuestions(updatedQuestions);
    };

    const removeAnswer = (questionIndex, answerIndex) => {
        const updatedQuestions = [...questions];
        const answers = [...(updatedQuestions[questionIndex].answer || [])];
        answers.splice(answerIndex, 1);
        updatedQuestions[questionIndex].answer = answers;
        setQuestions(updatedQuestions);
    };

    const deleteQuestion = (index) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    // Helper function to check if question contains a diagram
    const hasDiagram = (content) => {
        return content && content.includes('{#image');
    };

    // Helper function to safely parse coordinate answer
    const parseCoordinateAnswer = (answerArray) => {
        try {
            if (answerArray && answerArray[0]) {
                return JSON.parse(answerArray[0]);
            }
        } catch (e) {
            console.error('Error parsing coordinate answer:', e);
        }
        return { elements: [] };
    };

    // Helper function to stringify coordinate answer
    // Strips hydrated `points` arrays from curve elements before saving —
    // they are regenerated from `parsed` data on load by hydrateElements()
    const stringifyCoordinateAnswer = (answer) => {
        try {
            const cleaned = {
                ...answer,
                elements: (answer?.elements || []).map(el =>
                    el.type === 'curve' && el.points ? { ...el, points: undefined } : el
                ),
            };
            return JSON.stringify(cleaned);
        } catch (e) {
            console.error('Error stringifying coordinate answer:', e);
            return JSON.stringify({ elements: [] });
        }
    };

    // Helper function to safely parse table answer
    const parseTableAnswer = (answerArray) => {
        try {
            if (answerArray && answerArray[0]) {
                return JSON.parse(answerArray[0]);
            }
        } catch (e) {
            console.error('Error parsing table answer:', e);
        }
        return { rows: [] };
    };

    // Helper function to stringify table answer
    const stringifyTableAnswer = (answer) => {
        try {
            return JSON.stringify(answer);
        } catch (e) {
            console.error('Error stringifying table answer:', e);
            return JSON.stringify({ rows: [] });
        }
    };

    // Helper function to safely parse number line answer
    const parseNumberLineAnswer = (answerArray) => {
        try {
            if (answerArray && answerArray[0]) {
                return JSON.parse(answerArray[0]);
            }
        } catch (e) {
            console.error('Error parsing number line answer:', e);
        }
        return { elements: [] };
    };

    // Helper function to stringify number line answer
    const stringifyNumberLineAnswer = (answer) => {
        try {
            return JSON.stringify(answer);
        } catch (e) {
            console.error('Error stringifying number line answer:', e);
            return JSON.stringify({ elements: [] });
        }
    };

    // Helper function to get mapping answer from answer array
    const getMappingAnswer = (answerArray) => {
        return answerArray && answerArray[0] ? answerArray[0] : '';
    };

    // Update parent-child relationship
    const updateParentRelationship = (childIndex, newParentIndex) => {
        const updatedQuestions = [...questions];

        // Get old parent index
        const oldParentIndex = updatedQuestions[childIndex].parent_index;

        // Remove from old parent's children list
        if (oldParentIndex !== null && oldParentIndex !== undefined && updatedQuestions[oldParentIndex]) {
            updatedQuestions[oldParentIndex].children_indices =
                (updatedQuestions[oldParentIndex].children_indices || []).filter(idx => idx !== childIndex);
            if (updatedQuestions[oldParentIndex].children_indices.length === 0) {
                updatedQuestions[oldParentIndex].is_parent = false;
            }
        }

        // Set new parent
        if (newParentIndex !== null && newParentIndex !== undefined && newParentIndex !== '') {
            updatedQuestions[childIndex].parent_index = newParentIndex;

            // Add to new parent's children list
            if (!updatedQuestions[newParentIndex].children_indices) {
                updatedQuestions[newParentIndex].children_indices = [];
            }
            updatedQuestions[newParentIndex].children_indices.push(childIndex);
            updatedQuestions[newParentIndex].is_parent = true;
        } else {
            updatedQuestions[childIndex].parent_index = null;
        }

        setQuestions(updatedQuestions);
    };

    // Get potential parent options for a question
    const getParentOptions = (questionIndex) => {
        return questions
            .map((q, idx) => ({ ...q, originalIndex: idx }))
            .filter((q, idx) =>
                idx !== questionIndex && // Not self
                idx < questionIndex &&   // Must be before current question
                (q.parent_index === null || q.parent_index === undefined) // Not already a child
            );
    };

    /**
     * Re-solve a question using AI for the answer_tool the admin already chose.
     * The user must select an answer_tool first; this never overwrites it.
     */
    const handleResolveWithAI = async (index) => {
        const question = questions[index];
        if (!question.answer_tool) return; // button is disabled in this case

        setResolvingIndex(index);

        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/re-solve`,
                {
                    answer_tool: question.answer_tool,
                    question_content: question.content,
                    question_type: question.question_type,
                    passage: question.passage,
                    options: question.options,
                    answer_config: question.answer_config,
                },
                { headers: { Authorization: getAuthHeader() } }
            );

            const { answer, answer_config } = response.data;

            const updatedQuestions = [...questions];
            updatedQuestions[index] = {
                ...updatedQuestions[index],
                ...(answer !== undefined && { answer }),
                ...(answer_config && {
                    answer_config: { ...updatedQuestions[index].answer_config, ...answer_config },
                }),
                // NOTE: answer_tool is intentionally NOT overwritten — the admin owns it.
            };
            setQuestions(updatedQuestions);

            console.log(`Re-solved question ${index + 1} as ${question.answer_tool}:`, answer);

        } catch (error) {
            console.error('Re-solve failed:', error);
            onError('Failed to re-solve: ' + (error.response?.data?.error || error.message));
        } finally {
            setResolvingIndex(null);
        }
    };

    const handleSaveAll = async () => {
        if (questions.length === 0) {
            onError('No questions to save');
            return;
        }

        setSaving(true);

        // Process questions to wrap answers with delimiters if "Render as LaTeX" is checked
        const processedQuestions = questions.map(q => {
            // Mathlive with text-alternative uses {type,value} JSON per answer —
            // wrap the math-typed entries' values only, leave text entries alone.
            if (q.answer_tool === 'mathlive' && q.answer_config?.allowTextInput && Array.isArray(q.answer)) {
                if (!renderAsLatex) return q;
                return {
                    ...q,
                    answer: q.answer.map(ans => {
                        const parsed = parseMathliveAnswer(ans);
                        if (parsed.type !== MATHLIVE_ANSWER_TYPES.MATH) return ans;
                        return stringifyMathliveAnswer({
                            type: MATHLIVE_ANSWER_TYPES.MATH,
                            value: wrapLatexDelimiters(parsed.value),
                        });
                    }),
                };
            }
            if (renderAsLatex && (q.answer_tool === 'mathlive' || q.answer_tool === 'textfield' || !q.answer_tool) && Array.isArray(q.answer)) {
                return {
                    ...q,
                    answer: q.answer.map(ans => {
                        if (!ans || typeof ans !== 'string') return ans;
                        const trimmed = ans.trim();
                        // Check if already has delimiters
                        if (/^\\\(.*\\\)$/.test(trimmed) || /^\$.*\$$/.test(trimmed) || /^\\\[.*\\\]$/.test(trimmed)) {
                            return ans;
                        }
                        return `\\(${ans}\\)`;
                    })
                };
            }
            return q;
        });

        // Ensure graph questions have parsed data before saving
        const finalQuestions = processedQuestions.map(q => {
            if (q.answer_tool === 'polynomialGraph' && q.answer_config?.equation && !q.answer_config?.parsedPolynomial) {
                try {
                    const parsed = parseFactoredPolynomial(q.answer_config.equation);
                    const seed = q.answer_config.variationSeed || Date.now();
                    const { correctIndex } = generateGraphVariations(parsed, q.answer_config.interceptType || 'x', seed);
                    return {
                        ...q,
                        answer_config: { ...q.answer_config, parsedPolynomial: parsed, variationSeed: seed, correctGraphIndex: correctIndex },
                        answer: [JSON.stringify({ correctGraphIndex: correctIndex })],
                    };
                } catch (e) {
                    console.warn('Failed to parse polynomial equation on save:', e);
                    return q;
                }
            }
            if (q.answer_tool === 'rationalGraph' && q.answer_config?.equation && !q.answer_config?.parsedRational) {
                try {
                    const parsed = parseFactoredRational(q.answer_config.equation);
                    const seed = q.answer_config.variationSeed || Date.now();
                    const step1Types = normalizeStep1Types(q.answer_config);
                    const { correctIndex } = generateRationalGraphVariations(parsed, step1Types, seed);
                    return {
                        ...q,
                        answer_config: { ...q.answer_config, parsedRational: parsed, variationSeed: seed, correctGraphIndex: correctIndex },
                        answer: [JSON.stringify({ correctGraphIndex: correctIndex })],
                    };
                } catch (e) {
                    console.warn('Failed to parse rational equation on save:', e);
                    return q;
                }
            }
            if (q.answer_tool === 'trigGraph' && q.answer_config?.equation && !q.answer_config?.parsedTrig) {
                try {
                    const parsed = parseTrigEquation(q.answer_config.equation);
                    const seed = q.answer_config.variationSeed || Date.now();
                    const { correctIndex } = generateTrigGraphVariations(parsed, q.answer_config.step1Types || ['period'], seed);
                    return {
                        ...q,
                        answer_config: { ...q.answer_config, parsedTrig: parsed, variationSeed: seed, correctGraphIndex: correctIndex },
                        answer: [JSON.stringify({ correctGraphIndex: correctIndex })],
                    };
                } catch (e) {
                    console.warn('Failed to parse trig equation during save:', e);
                    return q;
                }
            }
            // Strip hydrated points from coordinate curve answers (regenerated on load)
            if (q.answer_tool === 'coordinate' && Array.isArray(q.answer) && q.answer[0]) {
                try {
                    const parsed = typeof q.answer[0] === 'string' ? JSON.parse(q.answer[0]) : q.answer[0];
                    if (parsed?.elements?.some(el => el.type === 'curve' && el.points)) {
                        const cleaned = {
                            ...parsed,
                            elements: parsed.elements.map(el =>
                                el.type === 'curve' && el.points ? { ...el, points: undefined } : el
                            ),
                        };
                        return { ...q, answer: [JSON.stringify(cleaned)] };
                    }
                } catch (e) {
                    // leave as-is if parse fails
                }
            }
            return q;
        });

        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/bulk`,
                {
                    questions: finalQuestions,
                },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            onSave();
        } catch (error) {
            console.error('Save error:', error);
            onError(error.response?.data?.error || 'Failed to save questions');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDraft = async () => {
        if (!extractionJobId) {
            onError('No extraction job ID available');
            return;
        }

        if (questions.length === 0) {
            onError('No questions to save');
            return;
        }

        setSavingDraft(true);

        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/extraction-jobs/${extractionJobId}`,
                {
                    extracted_questions: questions,
                },
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            if (onSaveDraft) {
                onSaveDraft();
            }
        } catch (error) {
            console.error('Save draft error:', error);
            onError(error.response?.data?.error || 'Failed to save extraction job');
        } finally {
            setSavingDraft(false);
        }
    };

    return (
        <Box>
            <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                    <Typography variant="h5" fontWeight="bold">
                        Review Extracted Questions ({questions.length})
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={isPreviewMode ? <EditIcon /> : <VisibilityIcon />}
                        onClick={() => setIsPreviewMode(!isPreviewMode)}
                    >
                        {isPreviewMode ? 'Edit Mode' : 'Preview Mode'}
                    </Button>
                </Box>
                <Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto' }}>
                        <Checkbox
                            checked={renderAsLatex}
                            onChange={(e) => setRenderAsLatex(e.target.checked)}
                            size="small"
                        />
                        <Typography variant="body2">Render answers as LaTeX</Typography>
                    </Box>
                    <Button variant="outlined" color="error" onClick={onCancel} disabled={saving || savingDraft}>
                        Cancel
                    </Button>
                    {extractionJobId && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleSaveDraft}
                            disabled={saving || savingDraft || questions.length === 0}
                        >
                            {savingDraft ? 'Saving Draft...' : 'Save Draft'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSaveAll}
                        disabled={saving || savingDraft || questions.length === 0}
                    >
                        {saving ? 'Saving...' : `Save All ${questions.length} Questions`}
                    </Button>
                </Box>
            </Box>

            {questions.length === 0 ? (
                <Paper elevation={2} sx={{ padding: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">
                        No questions to review
                    </Typography>
                </Paper>
            ) : (
                <Box display="flex" flexDirection="column" gap={3}>
                    {questions.map((question, index) => (
                        <Card key={index} elevation={3}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="h6" fontWeight="bold" color={colors.greenAccent[500]}>
                                            Question {index + 1}
                                        </Typography>
                                        {hasDiagram(question.content) && (
                                            <Chip
                                                icon={<ImageIcon />}
                                                label="Has Diagram"
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {/* Re-solve with AI button — uses whichever answer tool the admin selected */}
                                        {(() => {
                                            const toolLabel = answerToolsWithNone.find(t => t.value === question.answer_tool)?.label;
                                            const hasTool = !!question.answer_tool;
                                            const tooltipText = hasTool
                                                ? `Re-solve with AI as ${toolLabel}`
                                                : 'Pick an answer tool first';
                                            return (
                                                <Tooltip title={tooltipText}>
                                                    <span>
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => handleResolveWithAI(index)}
                                                            disabled={!hasTool || resolvingIndex === index || saving || savingDraft}
                                                            size="small"
                                                        >
                                                            {resolvingIndex === index ? (
                                                                <CircularProgress size={20} />
                                                            ) : (
                                                                <AutoFixHighIcon />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            );
                                        })()}

                                        {/* Delete button */}
                                        {!isPreviewMode && (
                                            <IconButton color="error" onClick={() => deleteQuestion(index)} size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>

                                {isPreviewMode ? (
                                    // Preview Mode - Read-only display
                                    <Box>
                                        {/* Question metadata */}
                                        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                                            {question.question_label && (
                                                <Chip label={`Label: ${question.question_label}`} size="small" />
                                            )}
                                            {question.page && (
                                                <Chip
                                                    label={`Page: ${question.page}`}
                                                    size="small"
                                                    onClick={onPageClick ? () => onPageClick(question.page) : undefined}
                                                    sx={onPageClick ? {
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            transform: 'scale(1.03)',
                                                        },
                                                        transition: 'transform 0.2s ease',
                                                    } : {}}
                                                    color={onPageClick ? "secondary" : "default"}
                                                />
                                            )}
                                            {question.question_type && (
                                                <Chip
                                                    label={questionTypes.find(t => t.value === question.question_type)?.label || question.question_type}
                                                    size="small"
                                                    color="primary"
                                                />
                                            )}
                                            {(
                                                <Tooltip
                                                    title={question.answer_tool_classification ? (
                                                        <Box>
                                                            <Typography variant="caption" display="block">
                                                                <strong>Pre-classification:</strong> {question.answer_tool_classification.answer_tool || 'None'}
                                                            </Typography>
                                                            <Typography variant="caption" display="block">
                                                                <strong>Confidence:</strong> {question.answer_tool_classification.confidence}
                                                            </Typography>
                                                            {question.answer_tool_classification.reasoning && (
                                                                <Typography variant="caption" display="block">
                                                                    <strong>Reasoning:</strong> {question.answer_tool_classification.reasoning}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ) : ''}
                                                    arrow
                                                    placement="top"
                                                >
                                                    <Chip
                                                        label={`Answer Tool: ${answerToolsWithNone.find(t => t.value === question.answer_tool)?.label || question.answer_tool || 'None'}`}
                                                        size="small"
                                                        sx={question.answer_tool_classification ? {
                                                            borderLeft: `3px solid ${
                                                                question.answer_tool_classification.confidence === 'high' ? '#4caf50' :
                                                                question.answer_tool_classification.confidence === 'medium' ? '#ff9800' : '#f44336'
                                                            }`,
                                                        } : {}}
                                                    />
                                                </Tooltip>
                                            )}
                                            {question.grading_type && (
                                                <Chip
                                                    label={`Auto-Grade (${GRADING_TYPE_LABELS[question.grading_type] || question.grading_type})`}
                                                    size="small"
                                                    color="secondary"
                                                />
                                            )}
                                            {/* Parent-child relationship indicators */}
                                            {question.is_parent && (
                                                <Chip
                                                    icon={<AccountTreeIcon />}
                                                    label={`Parent (${question.children_indices?.length || 0} children)`}
                                                    size="small"
                                                    color="success"
                                                />
                                            )}
                                            {(question.parent_index !== null && question.parent_index !== undefined) && (
                                                <Chip
                                                    icon={<AccountTreeIcon />}
                                                    label={`Child of Q${question.parent_index + 1}`}
                                                    size="small"
                                                    color="warning"
                                                />
                                            )}
                                            {/* Solving warning indicators */}
                                            {question.solving_warning === 'parent_unsolved' && (
                                                <Chip
                                                    icon={<WarningIcon />}
                                                    label="Review: Parent Unsolved"
                                                    size="small"
                                                    color="warning"
                                                />
                                            )}
                                            {question.solving_warning === 'solve_failed' && (
                                                <Chip
                                                    icon={<WarningIcon />}
                                                    label="Solve Failed"
                                                    size="small"
                                                    color="error"
                                                />
                                            )}
                                            {/* Context used indicator */}
                                            {question.ai_solving_context?.parent_was_solved && (
                                                <Chip
                                                    icon={<AccountTreeIcon />}
                                                    label={`Used answer from Q${question.parent_index + 1}`}
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>

                                        {/* Parent Context Display for Child Questions */}
                                        {(question.parent_index !== null && question.parent_index !== undefined) && (
                                            <Box sx={{
                                                mb: 2,
                                                p: 1.5,
                                                bgcolor: colors.blueAccent[900],
                                                borderRadius: 1,
                                                border: `1px solid ${colors.blueAccent[500]}`
                                            }}>
                                                <Typography variant="caption" color={colors.blueAccent[300]} fontWeight="bold">
                                                    This is a child of Question {question.parent_index + 1}
                                                    {questions[question.parent_index]?.question_label &&
                                                        ` (${questions[question.parent_index].question_label})`}
                                                </Typography>
                                                {question.ai_solving_context?.parent_was_solved && (
                                                    <Typography variant="caption" display="block" color={colors.grey[400]} mt={0.5}>
                                                        Parent's answer used: {JSON.stringify(question.ai_solving_context.parent_answer_used)}
                                                    </Typography>
                                                )}
                                                {question.ai_solving_context?.solved_independently && (
                                                    <Typography variant="caption" display="block" color={colors.redAccent[400]} mt={0.5}>
                                                        Warning: Solved independently (parent had no answer)
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}

                                        {/* Children indicator for parent questions */}
                                        {question.is_parent && question.children_indices?.length > 0 && (
                                            <Box sx={{
                                                mb: 2,
                                                p: 1.5,
                                                bgcolor: colors.greenAccent[900],
                                                borderRadius: 1,
                                                border: `1px solid ${colors.greenAccent[500]}`
                                            }}>
                                                <Typography variant="caption" color={colors.greenAccent[300]} fontWeight="bold">
                                                    Parent Question - Children: {question.children_indices.map(i => `Q${i + 1}`).join(', ')}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Passage */}
                                        {question.passage && (
                                            <Box sx={{ mb: 2, p: 2, backgroundColor: colors.primary[600], borderRadius: '8px', border: `1px solid ${colors.grey[600]}` }}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                                    Passage:
                                                </Typography>
                                                <QuestionRender content={question.passage} />
                                            </Box>
                                        )}

                                        {/* Question content */}
                                        {question.content && (
                                            <Box sx={{ mb: 2, p: 2, backgroundColor: colors.primary[600], borderRadius: '8px', border: `1px solid ${hasDiagram(question.content) ? colors.blueAccent[500] : colors.grey[600]}` }}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                                    Question:
                                                </Typography>
                                                <QuestionRender content={question.content} />
                                            </Box>
                                        )}

                                        {/* Options for multiple choice */}
                                        {question.question_type === 'multiple_choice' && question.options && question.options.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                                    Options:
                                                </Typography>
                                                {question.options.map((option, optionIndex) => (
                                                    <Box
                                                        key={optionIndex}
                                                        sx={{
                                                            mb: 1,
                                                            p: 1.5,
                                                            backgroundColor: colors.primary[600],
                                                            borderRadius: '4px',
                                                            border: `1px solid ${colors.grey[600]}`
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                                            Option {optionIndex + 1}:
                                                        </Typography>
                                                        <QuestionRender content={option} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        {/* Answers - Skip for coordinate/table/numberline/mapping/polynomialGraph since they have their own visual display */}
                                        {question.question_type !== 'file_upload' && question.answer_tool !== 'coordinate' && question.answer_tool !== 'table' && question.answer_tool !== 'numberline' && question.answer_tool !== 'mapping' && question.answer_tool !== 'polynomialGraph' && question.answer_tool !== 'rationalGraph' && question.answer_tool !== 'trigGraph' && question.answer_tool !== 'multiline' && question.answer && question.answer.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" fontWeight="bold" mb={1} color={colors.greenAccent[400]}>
                                                    Answer(s):
                                                </Typography>
                                                {question.question_type === 'multiple_choice' ? (
                                                    // For MC: support both index-based (new) and text-based (old) answers
                                                    (() => {
                                                        const answers = question.answer || [];
                                                        // Detect if answers are indices (numbers) or text (old format)
                                                        const isIndexBased = answers.length > 0 &&
                                                            answers.every(a => !isNaN(parseInt(a, 10)) && String(parseInt(a, 10)) === String(a));

                                                        return answers.map((ans, idx) => {
                                                            const displayText = isIndexBased
                                                                ? (question.options?.[parseInt(ans, 10)] || `Option ${parseInt(ans, 10) + 1}`)
                                                                : ans; // Old format: ans is already the text
                                                            const label = isIndexBased
                                                                ? `Correct Option (Index ${ans}):`
                                                                : `Correct Option:`;
                                                            return (
                                                                <Box
                                                                    key={idx}
                                                                    sx={{
                                                                        mb: 1,
                                                                        p: 1.5,
                                                                        backgroundColor: colors.greenAccent[900],
                                                                        borderRadius: '4px',
                                                                        border: `1px solid ${colors.greenAccent[500]}`
                                                                    }}
                                                                >
                                                                    <Typography variant="caption" color={colors.greenAccent[300]} display="block" mb={0.5}>
                                                                        {label}
                                                                    </Typography>
                                                                    <QuestionRender content={displayText} />
                                                                </Box>
                                                            );
                                                        });
                                                    })()
                                                ) : (
                                                    // For other types: answers are literal strings (or {type,value}
                                                    // JSON for mathlive with allowTextInput)
                                                    question.answer.map((answer, answerIndex) => {
                                                        const mathliveAllowsText = question.answer_tool === 'mathlive'
                                                            && question.answer_config?.allowTextInput === true;
                                                        const parsed = mathliveAllowsText
                                                            ? parseMathliveAnswer(answer)
                                                            : { type: MATHLIVE_ANSWER_TYPES.MATH, value: answer };
                                                        return (
                                                        <Box
                                                            key={answerIndex}
                                                            sx={{
                                                                mb: 1,
                                                                p: 1.5,
                                                                backgroundColor: colors.greenAccent[900],
                                                                borderRadius: '4px',
                                                                border: `1px solid ${colors.greenAccent[500]}`,
                                                                overflowX: 'auto',
                                                            }}
                                                        >
                                                            <Typography variant="caption" color={colors.greenAccent[300]} display="block" mb={0.5}>
                                                                Answer {answerIndex + 1}{mathliveAllowsText ? ` — ${parsed.type === MATHLIVE_ANSWER_TYPES.TEXT ? 'Text' : 'Math'}` : ''}:
                                                            </Typography>
                                                            {parsed.type === MATHLIVE_ANSWER_TYPES.TEXT ? (
                                                                <Typography>{parsed.value}</Typography>
                                                            ) : (
                                                                <QuestionRender content={parsed.value} />
                                                            )}
                                                        </Box>
                                                        );
                                                    })
                                                )}
                                            </Box>
                                        )}

                                        {/* Coordinate Grid Answer Preview */}
                                        {question.answer_tool === 'coordinate' && (() => {
                                            const config = question.answer_config?.grid || defaultCoordinateConfig.grid;
                                            const answerData = parseCoordinateAnswer(question.answer);
                                            const elements = answerData?.elements || [];

                                            return (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" mb={1} color={colors.greenAccent[400]}>
                                                        Correct Answer:
                                                    </Typography>

                                                    {/* Visual Grid Preview */}
                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                        <CoordinateGrid
                                                            config={config}
                                                            elements={elements}
                                                            pendingPoints={[]}
                                                            mode="point"
                                                            readOnly={true}
                                                            width={150}
                                                            height={150}
                                                        />

                                                        {/* Answer Summary with Chips */}
                                                        <Box sx={{ flex: 1, minWidth: 150 }}>
                                                            <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 1 }}>
                                                                {elements.length} element{elements.length !== 1 ? 's' : ''}:
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {elements.length === 0 ? (
                                                                    <Typography variant="caption" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
                                                                        No answer set
                                                                    </Typography>
                                                                ) : (
                                                                    elements.map((el, i) => (
                                                                        <Chip
                                                                            key={el.id || i}
                                                                            label={formatElementSummary(el)}
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: colors.greenAccent[900],
                                                                                borderColor: colors.greenAccent[500],
                                                                                color: colors.grey[200],
                                                                                fontSize: '0.7rem',
                                                                            }}
                                                                        />
                                                                    ))
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Box>

                                                    {/* Answer Validation Logic Summary */}
                                                    <AnswerLogicSummary
                                                        elements={elements}
                                                        answerConfig={question.answer_config}
                                                        compact={true}
                                                        colors={colors}
                                                    />
                                                </Box>
                                            );
                                        })()}

                                        {/* Table Answer Preview */}
                                        {question.answer_tool === 'table' && (() => {
                                            const config = question.answer_config || defaultTableConfig;
                                            const answerData = parseTableAnswer(question.answer);
                                            const rows = answerData?.rows || [];
                                            const columns = config.columns || [];

                                            // Count prefilled vs editable cells
                                            let prefilledCount = 0;
                                            let editableCount = 0;
                                            config.rows?.forEach(row => {
                                                columns.forEach(col => {
                                                    const cell = row.cells?.[col.id];
                                                    if (cell?.prefilled) {
                                                        prefilledCount++;
                                                    } else {
                                                        editableCount++;
                                                    }
                                                });
                                            });

                                            return (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" mb={1} color={colors.greenAccent[400]}>
                                                        Table Answer:
                                                    </Typography>

                                                    {/* Table Summary */}
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 1 }}>
                                                            {columns.length} column{columns.length !== 1 ? 's' : ''} ({columns.map(c => c.header || c.id).join(', ')}), {rows.length} row{rows.length !== 1 ? 's' : ''}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            <Chip
                                                                label={`${prefilledCount} prefilled`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: colors.blueAccent[900],
                                                                    color: colors.grey[200],
                                                                    fontSize: '0.7rem',
                                                                }}
                                                            />
                                                            <Chip
                                                                label={`${editableCount} editable`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: colors.greenAccent[900],
                                                                    color: colors.grey[200],
                                                                    fontSize: '0.7rem',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>

                                                    {/* Table Answer Validation Logic Summary */}
                                                    <TableAnswerLogicSummary
                                                        config={config}
                                                        compact={true}
                                                        colors={colors}
                                                        mode="full"
                                                    />
                                                </Box>
                                            );
                                        })()}

                                        {/* Number Line Answer Preview */}
                                        {question.answer_tool === 'numberline' && (() => {
                                            const config = question.answer_config || defaultNumberLineConfig;
                                            const answerData = parseNumberLineAnswer(question.answer);
                                            const elements = answerData?.elements || [];

                                            return (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" mb={1} color={colors.greenAccent[400]}>
                                                        Correct Answer:
                                                    </Typography>

                                                    {/* Visual Number Line Preview */}
                                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                        <NumberLine
                                                            config={config}
                                                            elements={elements}
                                                            readOnly={true}
                                                            width={400}
                                                            height={80}
                                                        />
                                                    </Box>

                                                    {/* Answer Summary with Chips */}
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 1 }}>
                                                            {elements.length} element{elements.length !== 1 ? 's' : ''}:
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {elements.length === 0 ? (
                                                                <Typography variant="caption" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
                                                                    No answer set
                                                                </Typography>
                                                            ) : (
                                                                elements.map((el, i) => (
                                                                    <Chip
                                                                        key={el.id || i}
                                                                        label={formatNumberLineElementSummary(el)}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: colors.greenAccent[900],
                                                                            borderColor: colors.greenAccent[500],
                                                                            color: colors.grey[200],
                                                                            fontSize: '0.7rem',
                                                                        }}
                                                                    />
                                                                ))
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })()}

                                        {/* Multiline Answer Preview */}
                                        {question.answer_tool === 'multiline' && (() => {
                                            const config = question.answer_config || defaultMultilineConfig;
                                            let answerData = { rows: [] };
                                            try {
                                                if (question.answer && question.answer[0]) {
                                                    answerData = JSON.parse(question.answer[0]);
                                                }
                                            } catch (e) {
                                                console.error('Error parsing multiline answer:', e);
                                            }
                                            const rows = answerData?.rows || [];

                                            return (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" mb={1} color={colors.greenAccent[400]}>
                                                        {config.gradingMode === 'manual' ? 'Sample Answers (manual grading):' : 'Correct Answers:'}
                                                    </Typography>

                                                    <Box sx={{
                                                        p: 1.5,
                                                        backgroundColor: colors.greenAccent[900],
                                                        borderRadius: '4px',
                                                        border: `1px solid ${colors.greenAccent[500]}`,
                                                    }}>
                                                        <MultilineReadOnly
                                                            config={config}
                                                            studentAnswer={answerData}
                                                            correctAnswer={answerData}
                                                            showCorrect={false}
                                                        />
                                                    </Box>

                                                    {/* Config summary */}
                                                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            label={`${rows.length} answer${rows.length !== 1 ? 's' : ''}`}
                                                            size="small"
                                                            sx={{ fontSize: '0.7rem' }}
                                                        />
                                                        <Chip
                                                            label={`Input: ${config.inputType || 'mathlive'}`}
                                                            size="small"
                                                            sx={{ fontSize: '0.7rem' }}
                                                        />
                                                        <Chip
                                                            label={`Grading: ${config.gradingMode || 'manual'}`}
                                                            size="small"
                                                            color={config.gradingMode === 'manual' ? 'warning' : 'info'}
                                                            sx={{ fontSize: '0.7rem' }}
                                                        />
                                                    </Box>
                                                </Box>
                                            );
                                        })()}

                                        {/* Mapping Answer Preview */}
                                        {question.answer_tool === 'mapping' && (() => {
                                            const config = question.answer_config || defaultMappingConfig;
                                            const answerStr = getMappingAnswer(question.answer);
                                            const answerData = parseMappingAnswer(answerStr);
                                            const connections = answerData?.connections || [];

                                            return (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" mb={1} color={colors.greenAccent[400]}>
                                                        Correct Answer:
                                                    </Typography>

                                                    {/* Visual Mapping Diagram Preview */}
                                                    <Box sx={{ maxWidth: 500 }}>
                                                        <MappingDiagram
                                                            leftItems={config.leftItems || []}
                                                            rightItems={config.rightItems || []}
                                                            connections={connections}
                                                            readOnly={true}
                                                            config={config}
                                                        />
                                                    </Box>

                                                    {/* Answer Summary */}
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mb: 1 }}>
                                                            {connections.length} connection{connections.length !== 1 ? 's' : ''}:
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {connections.length === 0 ? (
                                                                <Typography variant="caption" sx={{ color: colors.grey[500], fontStyle: 'italic' }}>
                                                                    No connections set
                                                                </Typography>
                                                            ) : (
                                                                connections.map((conn, i) => {
                                                                    const leftItem = config.leftItems?.find(item => item.id === conn.from);
                                                                    const rightItem = config.rightItems?.find(item => item.id === conn.to);
                                                                    return (
                                                                        <Chip
                                                                            key={i}
                                                                            label={`${leftItem?.value || 'Item'} → ${rightItem?.value || 'Item'}`}
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: colors.greenAccent[900],
                                                                                borderColor: colors.greenAccent[500],
                                                                                color: colors.grey[200],
                                                                                fontSize: '0.7rem',
                                                                            }}
                                                                        />
                                                                    );
                                                                })
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })()}
                                    </Box>
                                ) : (
                                    // Edit Mode - Existing editing interface
                                    <Box>
                                {question.answer_tool_classification && (
                                    <Box display="flex" gap={1} mb={1.5} alignItems="center">
                                        <Tooltip
                                            title={question.answer_tool_classification.reasoning || ''}
                                            arrow
                                            placement="top"
                                        >
                                            <Chip
                                                label={`Pre-classified: ${answerToolsWithNone.find(t => t.value === question.answer_tool_classification.answer_tool)?.label || question.answer_tool_classification.answer_tool || 'None'} (${question.answer_tool_classification.confidence})`}
                                                size="small"
                                                sx={{
                                                    borderLeft: `3px solid ${
                                                        question.answer_tool_classification.confidence === 'high' ? '#4caf50' :
                                                        question.answer_tool_classification.confidence === 'medium' ? '#ff9800' : '#f44336'
                                                    }`,
                                                }}
                                            />
                                        </Tooltip>
                                    </Box>
                                )}
                                <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2} mb={2}>
                                    <TextField
                                        label="Question Label"
                                        value={question.question_label || ''}
                                        onChange={(e) => updateQuestion(index, 'question_label', e.target.value)}
                                        size="small"
                                        required
                                    />
                                    <TextField
                                        label="Page"
                                        type="number"
                                        value={question.page ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const parsed = parseInt(value, 10);
                                            updateQuestion(index, 'page', isNaN(parsed) ? null : parsed);
                                        }}
                                        size="small"
                                        required
                                    />
                                    <Autocomplete
                                        options={questionTypes}
                                        value={
                                            questionTypes.find((type) => type.value === question.question_type) || null
                                        }
                                        onChange={(_, newValue) => {
                                            const nextType = newValue?.value || 'short_answer';
                                            // Reset tool/answer/options/config so a stale answer_tool
                                            // (e.g. numberline) doesn't hijack the save with its JSON blob.
                                            setQuestions(prev => {
                                                const updated = [...prev];
                                                updated[index] = {
                                                    ...updated[index],
                                                    question_type: nextType,
                                                    answer_tool: null,
                                                    answer: [],
                                                    options: nextType === 'multiple_choice' ? (updated[index].options || []) : [],
                                                    answer_config: null,
                                                };
                                                return updated;
                                            });
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Question Type" size="small" required />}
                                    />
                                    {question.question_type !== 'multiple_choice' &&
                                        question.question_type !== 'file_upload' && (
                                            <Autocomplete
                                                options={answerToolsWithNone}
                                                value={
                                                    answerToolsWithNone.find((tool) => tool.value === question.answer_tool) ||
                                                    null
                                                }
                                                onChange={(_, newValue) =>
                                                    handleAnswerToolChange(index, newValue?.value)
                                                }
                                                renderInput={(params) => <TextField {...params} label="Answer Tool" size="small" />}
                                            />
                                        )}
                                    {question.question_type === 'long_answer' && (
                                        <FormControl size="small">
                                            <InputLabel id={`grading-type-label-${index}`}>Grading Type</InputLabel>
                                            <Select
                                                labelId={`grading-type-label-${index}`}
                                                value={question.grading_type || ""}
                                                label="Grading Type"
                                                onChange={(e) => {
                                                    const value = e.target.value || null;
                                                    updateQuestion(index, 'grading_type', value);
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>Manual Grading</em>
                                                </MenuItem>
                                                {GRADING_TYPES.map((gt) => (
                                                    <MenuItem key={gt.value} value={gt.value}>{gt.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                    {/* Parent Question Selector */}
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                        <InputLabel id={`parent-question-label-${index}`}>Parent Question</InputLabel>
                                        <Select
                                            labelId={`parent-question-label-${index}`}
                                            value={question.parent_index ?? ''}
                                            label="Parent Question"
                                            onChange={(e) => {
                                                const newParentIndex = e.target.value === '' ? null : e.target.value;
                                                updateParentRelationship(index, newParentIndex);
                                            }}
                                            disabled={question.is_parent} // Can't be a child if already a parent
                                        >
                                            <MenuItem value="">
                                                <em>None (Standalone)</em>
                                            </MenuItem>
                                            {getParentOptions(index).map((parentOption) => (
                                                <MenuItem key={parentOption.originalIndex} value={parentOption.originalIndex}>
                                                    Q{parentOption.originalIndex + 1}: {parentOption.question_label || 'Untitled'}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {question.is_parent && (
                                            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                                                Cannot set parent (this question has children)
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Box>

                                {/* Coordinate Grid Configuration */}
                                {question.answer_tool === 'coordinate' && (
                                    <Box sx={{ mt: 2, mb: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                            Coordinate Grid Configuration
                                        </Typography>
                                        <CoordinateGridAdmin
                                            config={question.answer_config || defaultCoordinateConfig}
                                            onConfigChange={(newConfig) => updateQuestion(index, 'answer_config', newConfig)}
                                            correctAnswer={parseCoordinateAnswer(question.answer)}
                                            onAnswerChange={(answer) => updateQuestion(index, 'answer', [stringifyCoordinateAnswer(answer)])}
                                        />
                                    </Box>
                                )}

                                {/* Table Configuration */}
                                {question.answer_tool === 'table' && (
                                    <Box sx={{ mt: 2, mb: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                            Table Configuration
                                        </Typography>
                                        <TableAdmin
                                            key={`table-admin-${index}-${question.answer_tool}`}
                                            config={isValidTableConfig(question.answer_config) ? question.answer_config : JSON.parse(JSON.stringify(defaultTableConfig))}
                                            onConfigChange={(newConfig) => updateQuestion(index, 'answer_config', newConfig)}
                                            correctAnswer={parseTableAnswer(question.answer)}
                                            onAnswerChange={(answer) => updateQuestion(index, 'answer', [stringifyTableAnswer(answer)])}
                                        />

                                        {/* Table Answer Validation Logic Summary */}
                                        <TableAnswerLogicSummary
                                            config={question.answer_config || defaultTableConfig}
                                            compact={true}
                                            colors={colors}
                                            mode="full"
                                            title="Grading Rules Preview"
                                        />
                                    </Box>
                                )}

                                {/* Number Line Configuration */}
                                {question.answer_tool === 'numberline' && (
                                    <Box sx={{ mt: 2, mb: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                            Number Line Configuration
                                        </Typography>
                                        <NumberLineAdmin
                                            config={question.answer_config || defaultNumberLineConfig}
                                            onConfigChange={(newConfig) => updateQuestion(index, 'answer_config', newConfig)}
                                            correctAnswer={parseNumberLineAnswer(question.answer)}
                                            onAnswerChange={(answer) => updateQuestion(index, 'answer', [stringifyNumberLineAnswer(answer)])}
                                        />
                                    </Box>
                                )}

                                {/* Mapping Configuration */}
                                {question.answer_tool === 'mapping' && (
                                    <Box sx={{ mt: 2, mb: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                            Mapping/Matching Configuration
                                        </Typography>
                                        <MappingAdmin
                                            config={question.answer_config || defaultMappingConfig}
                                            answer={getMappingAnswer(question.answer)}
                                            onConfigChange={(newConfig) => updateQuestion(index, 'answer_config', newConfig)}
                                            onAnswerChange={(answer) => updateQuestion(index, 'answer', [answer])}
                                        />
                                    </Box>
                                )}

                                {/* Polynomial Graph Configuration */}
                                {question.answer_tool === 'polynomialGraph' && (
                                    <Box sx={{ mt: 2, mb: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                            Polynomial Graph Configuration
                                        </Typography>
                                        <PolynomialGraphAdmin
                                            config={question.answer_config || defaultPolynomialGraphConfig}
                                            onConfigChange={(newConfig) => updateQuestion(index, 'answer_config', newConfig)}
                                            correctAnswer={(() => {
                                                try {
                                                    return question.answer?.[0] ? JSON.parse(question.answer[0]) : { correctGraphIndex: 0 };
                                                } catch {
                                                    return { correctGraphIndex: 0 };
                                                }
                                            })()}
                                            onAnswerChange={(answer) => updateQuestion(index, 'answer', [JSON.stringify(answer)])}
                                        />
                                    </Box>
                                )}

                                {/* Rational Graph Configuration */}
                                {question.answer_tool === 'rationalGraph' && (
                                    <Box sx={{ mt: 2, mb: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                            Rational Graph Configuration
                                        </Typography>
                                        <RationalGraphAdmin
                                            config={question.answer_config || defaultRationalGraphConfig}
                                            onConfigChange={(newConfig) => updateQuestion(index, 'answer_config', newConfig)}
                                            correctAnswer={(() => {
                                                try {
                                                    return question.answer?.[0] ? JSON.parse(question.answer[0]) : { correctGraphIndex: 0 };
                                                } catch {
                                                    return { correctGraphIndex: 0 };
                                                }
                                            })()}
                                            onAnswerChange={(answer) => updateQuestion(index, 'answer', [JSON.stringify(answer)])}
                                        />
                                    </Box>
                                )}

                                {/* Trig Graph Configuration */}
                                {question.answer_tool === 'trigGraph' && (
                                    <Box sx={{ mt: 2, mb: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                            Trig Graph Configuration
                                        </Typography>
                                        <TrigGraphAdmin
                                            config={question.answer_config || defaultTrigGraphConfig}
                                            onConfigChange={(newConfig) => {
                                                const updatedQuestions = [...questions];
                                                updatedQuestions[index].answer_config = newConfig;
                                                setQuestions(updatedQuestions);
                                            }}
                                            correctAnswer={(() => {
                                                try {
                                                    return question.answer?.[0] ? JSON.parse(question.answer[0]) : { correctGraphIndex: 0 };
                                                } catch { return { correctGraphIndex: 0 }; }
                                            })()}
                                            onAnswerChange={(ans) => {
                                                const updatedQuestions = [...questions];
                                                updatedQuestions[index].answer = [JSON.stringify(ans)];
                                                setQuestions(updatedQuestions);
                                            }}
                                        />
                                    </Box>
                                )}

                                {question.answer_tool === 'multiline' && (
                                    <Box sx={{ mt: 2, mb: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                                            Multiline Configuration
                                        </Typography>
                                        <MultilineAdmin
                                            config={question.answer_config || defaultMultilineConfig}
                                            onConfigChange={(newConfig) => {
                                                const updatedQuestions = [...questions];
                                                updatedQuestions[index].answer_config = newConfig;
                                                setQuestions(updatedQuestions);
                                            }}
                                            correctAnswer={(() => {
                                                try {
                                                    return question.answer?.[0] ? JSON.parse(question.answer[0]) : { rows: [] };
                                                } catch { return { rows: [] }; }
                                            })()}
                                            onAnswerChange={(ans) => {
                                                const updatedQuestions = [...questions];
                                                updatedQuestions[index].answer = [JSON.stringify(ans)];
                                                setQuestions(updatedQuestions);
                                            }}
                                        />
                                    </Box>
                                )}

                                <TextFieldWithMath
                                    label="Passage (Optional)"
                                    value={question.passage || ''}
                                    onChange={(value) => updateQuestion(index, 'passage', value)}
                                    multiline
                                    rows={10}
                                    helperText="Shared context or reference text for this question (click ƒ button to insert math)"
                                    fullWidth
                                    sx={{ mb: 2 }}
                                />

                                {/* Preview for passage */}
                                {question.passage && (
                                    <Box
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            backgroundColor: colors.primary[600],
                                            borderRadius: '8px',
                                            border: `1px solid ${colors.grey[600]}`,
                                        }}
                                    >
                                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                            Passage Preview:
                                        </Typography>
                                        <QuestionRender content={question.passage} />
                                    </Box>
                                )}

                                <TextFieldWithMath
                                    label="Question Content"
                                    value={question.content || ''}
                                    onChange={(value) => updateQuestion(index, 'content', value)}
                                    multiline
                                    rows={10}
                                    required
                                    fullWidth
                                    sx={{ mb: 2 }}
                                />

                                {/* Preview for content */}
                                {question.content && (
                                    <Box
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            backgroundColor: colors.primary[600],
                                            borderRadius: '8px',
                                            border: `1px solid ${hasDiagram(question.content) ? colors.blueAccent[500] : colors.grey[600]}`,
                                        }}
                                    >
                                        <Typography variant="caption" color={hasDiagram(question.content) ? colors.blueAccent[300] : "textSecondary"} display="block" mb={0.5} fontWeight={hasDiagram(question.content) ? "bold" : "normal"}>
                                            {hasDiagram(question.content) ? 'Content Preview (with diagram):' : 'Content Preview:'}
                                        </Typography>
                                        <QuestionRender content={question.content} />
                                    </Box>
                                )}

                                {question.question_type === 'multiple_choice' && (
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                            Options
                                        </Typography>
                                        {(question.options || []).map((option, optionIndex) => (
                                            <Box key={optionIndex} display="flex" gap={2} mb={2} alignItems="flex-start">
                                                {/* Preview for option */}
                                                {option && (
                                                    <Box
                                                        sx={{
                                                            minWidth: '200px',
                                                            maxWidth: '200px',
                                                            p: 1.5,
                                                            backgroundColor: colors.primary[600],
                                                            borderRadius: '4px',
                                                            border: `1px solid ${colors.grey[600]}`,
                                                        }}
                                                    >
                                                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                                            Preview:
                                                        </Typography>
                                                        <QuestionRender content={option} />
                                                    </Box>
                                                )}
                                                <Box sx={{ flex: 1 }}>
                                                    <TextFieldWithMath
                                                        label={`Option ${optionIndex + 1}`}
                                                        value={option}
                                                        onChange={(value) =>
                                                            updateOption(index, optionIndex, value)
                                                        }
                                                        multiline
                                                        rows={2}
                                                    />
                                                </Box>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeOption(index, optionIndex)}
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        ))}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => addOption(index)}
                                            sx={{ mt: 1 }}
                                        >
                                            Add Option
                                        </Button>
                                    </Box>
                                )}

                                {/* Answer Section */}
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                                        Answer(s)
                                    </Typography>

                                    {question.question_type === 'multiple_choice' ? (
                                        (() => {
                                            // Detect if current answers are index-based or text-based (old format)
                                            const currentAnswers = question.answer || [];
                                            const isIndexBased = currentAnswers.length === 0 ||
                                                currentAnswers.every(a => !isNaN(parseInt(a, 10)) && String(parseInt(a, 10)) === String(a));

                                            // Helper to check if option is selected
                                            const isSelected = (optionIndex) => {
                                                if (isIndexBased) {
                                                    // Compare as strings to handle both number and string indices from JSON
                                                    return currentAnswers.map(String).includes(String(optionIndex));
                                                } else {
                                                    // Old format: check if option text is in answers
                                                    const options = question.options || [];
                                                    return currentAnswers.includes(options[optionIndex]);
                                                }
                                            };

                                            return (
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                                        Select correct option(s) - new selections use indices (0-based)
                                                    </Typography>
                                                    {(question.options || []).map((option, optionIndex) => (
                                                        <Box
                                                            key={optionIndex}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                mb: 1,
                                                                p: 1,
                                                                border: `1px solid ${isSelected(optionIndex) ? colors.greenAccent[500] : colors.grey[700]}`,
                                                                borderRadius: '4px',
                                                                backgroundColor: isSelected(optionIndex) ? colors.greenAccent[900] : 'transparent',
                                                            }}
                                                        >
                                                            <Checkbox
                                                                checked={isSelected(optionIndex)}
                                                                onChange={(e) => {
                                                                    // Always save as indices (new format) when editing
                                                                    // Convert old format to new format if needed
                                                                    let indexAnswers = [];
                                                                    if (isIndexBased) {
                                                                        // Normalize to numbers to ensure consistent storage
                                                                        indexAnswers = currentAnswers.map(a => parseInt(a, 10));
                                                                    } else {
                                                                        // Convert old text format to indices
                                                                        const options = question.options || [];
                                                                        indexAnswers = currentAnswers
                                                                            .map(textAns => options.indexOf(textAns))
                                                                            .filter(idx => idx !== -1);
                                                                    }

                                                                    const newAnswers = e.target.checked
                                                                        ? [...indexAnswers, optionIndex]
                                                                        : indexAnswers.filter(a => a !== optionIndex);
                                                                    updateQuestion(index, 'answer', newAnswers);
                                                                }}
                                                                sx={{ pt: 0 }}
                                                            />
                                                            <Box sx={{ flex: 1, pt: 0.5 }}>
                                                                <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                                                                    [{optionIndex}]
                                                                </Typography>
                                                                <QuestionRender content={option} />
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            );
                                        })()
                                    ) : question.question_type !== 'file_upload' && question.answer_tool !== 'coordinate' && question.answer_tool !== 'table' && question.answer_tool !== 'numberline' && question.answer_tool !== 'mapping' && question.answer_tool !== 'polynomialGraph' && question.answer_tool !== 'rationalGraph' && question.answer_tool !== 'trigGraph' && question.answer_tool !== 'multiline' ? (
                                        <Box>
                                            {question.answer_tool === 'mathlive' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                    <Checkbox
                                                        checked={question.answer_config?.allowTextInput === true}
                                                        onChange={(e) => {
                                                            const enabled = e.target.checked;
                                                            const nextConfig = {
                                                                ...defaultMathliveConfig,
                                                                ...(question.answer_config || {}),
                                                                allowTextInput: enabled,
                                                                ...(enabled && { spec: null }),
                                                            };
                                                            // Re-encode existing answers to match the new shape so subsequent
                                                            // edits stay consistent with what will be persisted.
                                                            const currentAnswers = Array.isArray(question.answer) ? question.answer : [];
                                                            let nextAnswers;
                                                            if (enabled) {
                                                                nextAnswers = currentAnswers.map(a => {
                                                                    const parsed = parseMathliveAnswer(a);
                                                                    return stringifyMathliveAnswer(parsed);
                                                                });
                                                            } else {
                                                                nextAnswers = currentAnswers.map(a => parseMathliveAnswer(a).value);
                                                            }
                                                            setQuestions(prev => {
                                                                const next = [...prev];
                                                                next[index] = {
                                                                    ...next[index],
                                                                    answer_config: nextConfig,
                                                                    answer: nextAnswers,
                                                                };
                                                                return next;
                                                            });
                                                        }}
                                                        size="small"
                                                    />
                                                    <Typography variant="body2">
                                                        Allow text answer (e.g. "no solutions", "infinitely many")
                                                    </Typography>
                                                </Box>
                                            )}
                                            {question.answer_tool === 'mathlive'
                                                && question.answer_config?.allowTextInput !== true && (
                                                <Box sx={{ mb: 2 }}>
                                                    {question.typed_spec_source === 'ai_extraction'
                                                        && question.answer_config?.spec && (
                                                        <Chip
                                                            size="small"
                                                            color="info"
                                                            label="AI-proposed typed specification — review before saving"
                                                            sx={{ mb: 0.5 }}
                                                        />
                                                    )}
                                                    <MathAnswerSpecEditor
                                                        questionContent={question.content}
                                                        passage={question.passage}
                                                        config={question.answer_config || defaultMathliveConfig}
                                                        onChange={(nextConfig) => setQuestions((prevQuestions) => {
                                                            const updatedQuestions = [...prevQuestions];
                                                            updatedQuestions[index] = {
                                                                ...updatedQuestions[index],
                                                                answer_config: nextConfig,
                                                                typed_spec_source: null,
                                                            };
                                                            return updatedQuestions;
                                                        })}
                                                    />
                                                </Box>
                                            )}
                                            {(question.answer || ['']).map((answer, answerIndex) => {
                                                const mathliveAllowsText = question.answer_tool === 'mathlive'
                                                    && question.answer_config?.allowTextInput === true;
                                                const parsed = mathliveAllowsText
                                                    ? parseMathliveAnswer(answer)
                                                    : { type: MATHLIVE_ANSWER_TYPES.MATH, value: answer };
                                                const commitMathlive = (nextType, nextValue) => {
                                                    updateAnswer(
                                                        index,
                                                        answerIndex,
                                                        stringifyMathliveAnswer({ type: nextType, value: nextValue })
                                                    );
                                                };
                                                return (
                                                <Box key={answerIndex} display="flex" gap={1} mb={2} alignItems="flex-start">
                                                    <Box sx={{ flex: 1 }}>
                                                        {question.answer_tool === 'mathlive' ? (
                                                            <>
                                                                {mathliveAllowsText && (
                                                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                                        <Chip
                                                                            label="Math"
                                                                            size="small"
                                                                            color={parsed.type === MATHLIVE_ANSWER_TYPES.MATH ? 'primary' : 'default'}
                                                                            onClick={() => commitMathlive(MATHLIVE_ANSWER_TYPES.MATH, parsed.value)}
                                                                        />
                                                                        <Chip
                                                                            label="Text"
                                                                            size="small"
                                                                            color={parsed.type === MATHLIVE_ANSWER_TYPES.TEXT ? 'primary' : 'default'}
                                                                            onClick={() => commitMathlive(MATHLIVE_ANSWER_TYPES.TEXT, parsed.value)}
                                                                        />
                                                                    </Box>
                                                                )}
                                                                {parsed.type === MATHLIVE_ANSWER_TYPES.TEXT ? (
                                                                    <TextField
                                                                        value={parsed.value || ''}
                                                                        onChange={(e) => commitMathlive(MATHLIVE_ANSWER_TYPES.TEXT, e.target.value)}
                                                                        fullWidth
                                                                        size="small"
                                                                        placeholder='Text answer (e.g. "no solutions")'
                                                                    />
                                                                ) : (
                                                                    <Box
                                                                        sx={{
                                                                            border: `1px solid ${colors.grey[400]}`,
                                                                            borderRadius: '4px',
                                                                            padding: '8px',
                                                                            backgroundColor: colors.primary[400],
                                                                            overflowX: 'auto',
                                                                        }}
                                                                    >
                                                                        <math-field
                                                                            value={parsed.value || ''}
                                                                            onInput={(evt) => {
                                                                                if (mathliveAllowsText) {
                                                                                    commitMathlive(MATHLIVE_ANSWER_TYPES.MATH, evt.target.value);
                                                                                } else {
                                                                                    updateAnswer(index, answerIndex, evt.target.value);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                width: '100%',
                                                                                border: 'none',
                                                                                fontSize: '16px',
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TextField
                                                                    value={answer || ''}
                                                                    onChange={(e) => updateAnswer(index, answerIndex, e.target.value)}
                                                                    multiline
                                                                    rows={2}
                                                                    fullWidth
                                                                    size="small"
                                                                    placeholder="Enter an acceptable answer"
                                                                />
                                                                {/* Preview for answer */}
                                                                {answer && (
                                                                    <Box
                                                                        sx={{
                                                                            mt: 1,
                                                                            p: 1.5,
                                                                            backgroundColor: colors.primary[600],
                                                                            borderRadius: '4px',
                                                                            border: `1px solid ${colors.grey[600]}`,
                                                                        }}
                                                                    >
                                                                        <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                                                            Preview:
                                                                        </Typography>
                                                                        <QuestionRender content={answer} />
                                                                    </Box>
                                                                )}
                                                            </>
                                                        )}
                                                    </Box>
                                                    {(question.answer || []).length > 1 && (
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => removeAnswer(index, answerIndex)}
                                                            size="small"
                                                            sx={{ mt: 1 }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                );
                                            })}
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => addAnswer(index)}
                                                sx={{ mt: 1 }}
                                            >
                                                Add Alternative Answer
                                            </Button>
                                        </Box>
                                    ) : null}
                                </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {questions.length > 0 && (
                <Box display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1} mt={3} alignItems="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto' }}>
                        <Checkbox
                            checked={renderAsLatex}
                            onChange={(e) => setRenderAsLatex(e.target.checked)}
                            size="small"
                        />
                        <Typography variant="body2">Render answers as LaTeX</Typography>
                    </Box>
                    <Button variant="outlined" color="error" onClick={onCancel} disabled={saving || savingDraft}>
                        Cancel
                    </Button>
                    {extractionJobId && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleSaveDraft}
                            disabled={saving || savingDraft || questions.length === 0}
                        >
                            {savingDraft ? 'Saving Draft...' : 'Save Draft'}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleSaveAll}
                        disabled={saving || savingDraft}
                    >
                        {saving ? 'Saving...' : `Save All ${questions.length} Questions`}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ExtractedQuestionsReview;
